import axios from 'axios';
import { get_token } from '@/router';
import { PLATFORM_IDS, get_platform_config, platform_id_for_name } from './platforms_config';
import { is_super_admin, safe_redirect_url } from './platform_switcher_helpers';

function auth_headers() {
  return {
    Authorization: `Token ${get_token()}`,
    'Content-Type': 'application/json',
  };
}

function enabled_platform_names(payload) {
  return (payload?.data || [])
    .filter(record => String(record.status || '').toUpperCase() === 'ENABLED')
    .map(record => record.platform_name)
    .filter(Boolean);
}

async function fetch_platform_access_payload() {
  const url = `${process.env.VUE_APP_BACKEND_API_URL}/system_management/api/get_user_platform_access/`;
  try {
    const response = await axios.get(url, { headers: auth_headers() });
    if (response.data?.status !== 'success') {
      throw new Error(response.data?.message || 'Unable to load platform access');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Unable to load platform access');
  }
}

const ALWAYS_VISIBLE_PLATFORM_IDS = ['aic_one'];

function ensure_always_visible_platforms_included(platform_ids, current_platform_id) {
  const merged = new Set(platform_ids);
  ALWAYS_VISIBLE_PLATFORM_IDS.forEach((id) => {
    merged.add(id);
  });
  if (current_platform_id) merged.add(current_platform_id);
  return Array.from(merged);
}

export async function fetch_accessible_platforms(current_platform_id) {
  let platform_ids;

  if (is_super_admin()) {
    platform_ids = [...PLATFORM_IDS];
  } else {
    const token = get_token();
    if (!token) {
      platform_ids = [];
    } else {
      const payload = await fetch_platform_access_payload();
      const ids = enabled_platform_names(payload)
        .map(name => platform_id_for_name(name))
        .filter(Boolean);
      platform_ids = Array.from(new Set(ids));
    }
  }

  platform_ids = ensure_always_visible_platforms_included(platform_ids, current_platform_id);

  return platform_ids
    .map(id => get_platform_config(id))
    .filter(Boolean);
}

async function request_platform_launch(platform) {
  if (!get_token()) throw new Error('Not authenticated');

  const url = `${process.env.VUE_APP_BACKEND_API_URL}/system_management/api/launch_platform/`;
  const data = {
    platform: platform.app_code,
    external_user_id: localStorage.getItem('user_id'),
  };

  try {
    const response = await axios.post(url, data, { headers: auth_headers() });
    if (!response.data?.redirect_url) {
      throw new Error('Unable to launch platform');
    }
    return response.data.redirect_url;
  } catch (error) {
    const server_message = error.response?.data?.error || error.response?.data?.detail;
    throw new Error(server_message || error.message || 'Unable to launch platform');
  }
}

export async function launch_platform(platform) {
  if (!platform.app_code) {
    window.location.assign(platform.launch_path || '/home');
    return;
  }

  const redirect_url = await request_platform_launch(platform);
  const safe_url = safe_redirect_url(redirect_url);
  if (!safe_url) throw new Error('Platform launch URL is not approved');

  if (platform.opens_in_new_tab) {
    window.open(safe_url, '_blank', 'noopener');
  } else {
    window.location.assign(safe_url);
  }
}

